import { useApolloClient, useMutation } from '@apollo/client';
import {
  SUBMISSION_FINISH_MUTATION,
  SubmissionFinishMutationData,
  SubmissionFinishMutationVariables,
} from '../graphql/mutation/submission.finish.mutation';
import {
  SUBMISSION_SET_FIELD_MUTATION,
  SubmissionSetFieldMutationData,
  SubmissionSetFieldMutationVariables,
} from '../graphql/mutation/submission.set.field.mutation';
import {
  SUBMISSION_START_MUTATION,
  SubmissionStartMutationData,
  SubmissionStartMutationVariables,
} from '../graphql/mutation/submission.start.mutation';
import { Data, QUERY, Variables } from '@/graphql/query/submission.public.query';

export interface Submission {
  startSubmission: () => Promise<{ id: string; token: string } | undefined>;
  setField: (
    fieldId: string,
    data: unknown,
    submission: { id: string; token: string }
  ) => Promise<void>;
  finish: (submission: { id: string; token: string }) => Promise<void>;
  fetchSubmissionById: (submissionId: string) => Promise<any>;
}

export const useSubmission = (formId: string): Submission => {
  const [start] = useMutation<
    SubmissionStartMutationData,
    SubmissionStartMutationVariables
  >(SUBMISSION_START_MUTATION);
  const [save] = useMutation<
    SubmissionSetFieldMutationData,
    SubmissionSetFieldMutationVariables
  >(SUBMISSION_SET_FIELD_MUTATION);
  const [submit] = useMutation<
    SubmissionFinishMutationData,
    SubmissionFinishMutationVariables
  >(SUBMISSION_FINISH_MUTATION);

  const client = useApolloClient();

  // creates a new empty submission and inputs into the system
  const startSubmission = async () => {
    const token = [...Array(40)]
      .map(() => Math.random().toString(36)[2])
      .join('');
    console.log('token: ' + token);

    const fetchedData = await start({
      variables: {
        form: formId,
        submission: {
          token,
          device: {
            name: /Mobi/i.test(window.navigator.userAgent)
              ? 'mobile'
              : 'desktop',
            type: window.navigator.userAgent,
          },
        },
      },
    });
    console.log('fetchedData', fetchedData);
    if (fetchedData.errors) {
      console.error(
        'failed to start submission %J' + fetchedData.errors.toString
      );
      return undefined;
    } else {
      return {
        id: fetchedData.data!.submission.id,
        token,
      };
    }
  };

  const setField = async (
    fieldId: string,
    data: any,
    submission: { id: string; token: string }
  ) => {
    if (data === undefined || data === null || !submission) {
      console.log('skip save field id=%O %O', fieldId, data);
      return;
    }

    const field = data.map((obj: any) => ({
      ...obj,
      token: submission.token,
    }));
    window.localStorage.setItem(fieldId, JSON.stringify(data));
    await save({
      variables: {
        submission: submission.id,
        fields: field,
      },
    });
  };

  const finish = async (submission: { id: string; token: string }) => {
    if (submission) {
      await submit({
        variables: {
          submission: submission.id,
        },
      });
    }
  };

  const fetchSubmissionById = async (submissionId: string) => {
    try {
      const { data } = await client.query<Data, Variables>({
        query: QUERY,
        variables: { id: submissionId },
        fetchPolicy: 'network-only', // ensures fresh data
      });
      const res = data?.submission ?? null;
      console.log('use submission fetchSubmissionById', res);
      return res;
    } catch (error) {
      console.error('Error fetching submission:', error);
      return null;
    }
  };

  return {
    startSubmission,
    setField,
    finish,
    fetchSubmissionById,
  };
};
